import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
} from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ENDPOINTS, apiCall } from '../utils/api.js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// The card-entry form; lives inside <Elements> so it can use the Stripe hooks.
function AddCardForm({ orgId, onDone, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements, redirect: 'if_required', confirmParams: { return_url: window.location.href },
    });
    if (error) { onError(error.message || 'Could not save the card.'); setBusy(false); return; }
    // Make the newly-added card the default for future invoices.
    try {
      await apiCall(ENDPOINTS.ORG_SET_DEFAULT_PM(orgId), {
        method: 'POST', body: JSON.stringify({ payment_method: setupIntent.payment_method }),
      });
    } catch { /* card is added even if setting default fails */ }
    setBusy(false);
    onDone();
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      <Button type="submit" variant="contained" fullWidth disabled={!stripe || busy} sx={{ mt: 2 }}>
        {busy ? <CircularProgress size={18} /> : 'Save card'}
      </Button>
    </form>
  );
}

// Embedded billing management: cards, invoices, retry failed payment, billing details, coupon.
export default function BillingManageCard({ orgId, sub, onChanged, refreshKey = 0 }) {
  const [cards, setCards] = useState([]);
  const [clientSecret, setClientSecret] = useState(null);  // add-card dialog open when set
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState({ name: '', email: '', line1: '', city: '', state: '', postal_code: '', country: '' });
  const [coupon, setCoupon] = useState('');

  const reload = useCallback(async () => {
    if (!orgId) return;
    const pm = await apiCall(ENDPOINTS.ORG_PAYMENT_METHODS(orgId)).catch(() => null);
    setCards((pm && pm.payment_methods) || []);
  }, [orgId]);
  // `refreshKey` is bumped by the billing page's Refresh button — re-fetch when it changes.
  // eslint-disable-next-line
  useEffect(() => { reload(); }, [reload, refreshKey]);

  const openAddCard = async () => {
    setMsg('');
    try {
      const res = await apiCall(ENDPOINTS.ORG_SETUP_INTENT(orgId), { method: 'POST' });
      if (res && res.client_secret) setClientSecret(res.client_secret);
      else setMsg('Could not start card setup.');
    } catch (e) { setMsg(e.message || 'Could not start card setup.'); }
  };
  const onCardSaved = async () => { setClientSecret(null); setMsg('Card saved.'); await reload(); onChanged && onChanged(); };

  const act = async (fn, ok) => {
    setBusy(true); setMsg('');
    try { await fn(); if (ok) setMsg(ok); await reload(); onChanged && onChanged(); }
    catch (e) { setMsg((e && e.message) || 'Something went wrong.'); }
    setBusy(false);
  };
  const setDefault = (pm) => act(() => apiCall(ENDPOINTS.ORG_SET_DEFAULT_PM(orgId), { method: 'POST', body: JSON.stringify({ payment_method: pm }) }), 'Default card updated.');
  const removeCard = (pm) => act(() => apiCall(ENDPOINTS.ORG_REMOVE_PM(orgId), { method: 'POST', body: JSON.stringify({ payment_method: pm }) }), 'Card removed.');
  const retryPayment = () => act(() => apiCall(ENDPOINTS.ORG_RETRY_PAYMENT(orgId), { method: 'POST' }), 'Payment retried.');
  const applyCoupon = () => act(async () => {
    const res = await apiCall(ENDPOINTS.ORG_APPLY_COUPON(orgId), { method: 'POST', body: JSON.stringify({ code: coupon }) });
    if (res && res.status !== 'Success') throw new Error(res.message || 'Invalid code.');
    setCoupon('');
  }, 'Coupon applied.');
  const openDetails = async () => {
    setMsg('');
    try {
      const res = await apiCall(ENDPOINTS.ORG_BILLING_DETAILS(orgId));  // GET current values
      const bd = (res && res.billing_details) || {};
      const a = bd.address || {};
      setDetails({
        name: bd.name || '', email: bd.email || '', line1: a.line1 || '', city: a.city || '',
        state: a.state || '', postal_code: a.postal_code || '', country: a.country || '',
      });
    } catch { /* open blank if we can't load */ }
    setDetailsOpen(true);
  };
  const saveDetails = () => act(async () => {
    const { name, email, ...address } = details;
    await apiCall(ENDPOINTS.ORG_BILLING_DETAILS(orgId), {
      method: 'POST', body: JSON.stringify({ name, email, address }),
    });
    setDetailsOpen(false);
  }, 'Billing details updated.');

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Payment &amp; billing</Typography>

      {/* Past-due dunning recovery */}
      {sub && sub.status === 'past_due' && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}
          action={<Button size="small" color="inherit" disabled={busy} onClick={retryPayment}>Retry payment</Button>}>
          Your last payment failed. Update your card or retry the charge.
        </Alert>
      )}

      {/* Saved cards */}
      <Stack spacing={1}>
        {cards.length === 0 && <Typography variant="caption" color="text.secondary">No saved payment methods.</Typography>}
        {cards.map((c) => (
          <Stack key={c.id} direction="row" spacing={1} alignItems="center">
            <CreditCardOutlinedIcon fontSize="small" color="action" />
            {/* `label` is built server-side so the UI doesn't need to know every Stripe
                payment-method type (a card shows brand+last4, Link shows its email, etc).
                Expiry only exists on cards. */}
            <Typography variant="body2" sx={{ flex: 1 }}>
              {c.label || `${(c.brand || 'card').toUpperCase()} •••• ${c.last4 || ''}`}
              {c.exp_month && c.exp_year && (
                <Typography component="span" variant="caption" color="text.secondary"> exp {c.exp_month}/{c.exp_year}</Typography>
              )}
            </Typography>
            {c.is_default
              ? <Chip size="small" label="Default" sx={{ height: 20 }} />
              : <Button size="small" disabled={busy} onClick={() => setDefault(c.id)}>Make default</Button>}
            {!c.is_default && (
              <IconButton size="small" disabled={busy} onClick={() => removeCard(c.id)} aria-label="remove card">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" onClick={openAddCard} disabled={busy}>Add / update card</Button>
        <Button size="small" onClick={openDetails} disabled={busy}>Billing details</Button>
      </Stack>

      {/* Coupon */}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <TextField size="small" label="Promo code" value={coupon} onChange={(e) => setCoupon(e.target.value)} sx={{ maxWidth: 180 }} />
        <Button size="small" onClick={applyCoupon} disabled={busy || !coupon.trim()}>Apply</Button>
      </Stack>

      {msg && <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>{msg}</Alert>}

      {/* Add-card dialog with Stripe PaymentElement */}
      <Dialog open={!!clientSecret} onClose={() => setClientSecret(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add / update card</DialogTitle>
        <DialogContent>
          {clientSecret && (
            <Box sx={{ mt: 1 }}>
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <AddCardForm orgId={orgId} onDone={onCardSaved} onError={(m) => setMsg(m)} />
              </Elements>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Billing details dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Billing details</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {['name', 'email', 'line1', 'city', 'state', 'postal_code', 'country'].map((f) => (
              <TextField key={f} size="small" label={f === 'line1' ? 'Address' : f.replace('_', ' ')}
                value={details[f]} onChange={(e) => setDetails((d) => ({ ...d, [f]: e.target.value }))} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDetails} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
